import { useState, useEffect, useRef, useCallback } from 'react';
import { MapPin, Loader2, Search, Clock } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { searchAddress, getRecentAddresses, addRecentAddress, retrieveMapboxSuggestion } from '@/lib/geo';

/**
 * AddressAutocomplete — free Nominatim (OpenStreetMap) powered input with
 * 24h response cache + recent-addresses surfaced when the input is empty
 * or only 1-2 chars typed.
 */
export default function AddressAutocomplete({
  value = '',
  onChange,
  placeholder = 'Start typing an address…',
  accent = 'blue',
  testId,
  anchor = null
}) {
  const [query, setQuery] = useState(value);
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recents, setRecents] = useState([]);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);
  const containerRef = useRef(null);
  const skipNextSearchRef = useRef(false);

  useEffect(() => { setQuery(value); }, [value]);
  useEffect(() => { setRecents(getRecentAddresses()); }, []);

  // Debounced search on query change
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (skipNextSearchRef.current) {
      skipNextSearchRef.current = false;
      return;
    }
    if (!query || query.trim().length < 3) {
      setSuggestions([]);
      setLoading(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      if (abortRef.current) abortRef.current.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      setLoading(true);
      try {
        const results = await searchAddress(query, { signal: ctrl.signal, anchor });
        setSuggestions(results);
        setOpen(results.length > 0);
      } catch {
        // aborted or failed — ignore
      } finally {
        setLoading(false);
      }
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [query, anchor]);

  // Close on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const handlePick = useCallback(async (sug) => {
    skipNextSearchRef.current = true;
    setQuery(sug.short);
    setSuggestions([]);
    setOpen(false);

    // Mapbox suggestions don't carry coords — we have to /retrieve to get them.
    // Recents and Nominatim results already have lat/lng so we skip retrieve.
    let resolved = sug;
    if (sug._needsRetrieve) {
      setLoading(true);
      try {
        const r = await retrieveMapboxSuggestion(sug);
        if (r && r.lat != null && r.lng != null) resolved = r;
      } finally {
        setLoading(false);
      }
    }

    addRecentAddress(resolved);
    setRecents(getRecentAddresses());
    onChange?.(resolved.short, { lat: resolved.lat, lng: resolved.lng }, { city: resolved.city, state: resolved.state });
  }, [onChange]);

  const handleType = (e) => {
    const v = e.target.value;
    setQuery(v);
    onChange?.(v, null, null);
  };

  const handleFocus = () => {
    // Show recents when input is empty/short and we have history
    if ((!query || query.trim().length < 3) && recents.length > 0) {
      setOpen(true);
    } else if (suggestions.length > 0) {
      setOpen(true);
    }
  };

  const accentClass = accent === 'emerald' ? 'text-[#00E676]' : 'text-[#4D88FF]';

  // What to show in the dropdown: live results take precedence, otherwise recents
  const showingRecents = (!query || query.trim().length < 3) && recents.length > 0 && suggestions.length === 0;
  const items = showingRecents ? recents : suggestions;

  return (
    <div ref={containerRef} className="relative" data-testid={testId ? `${testId}-wrapper` : undefined}>
      <div className="relative">
        <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${accentClass}`} />
        <Input
          value={query}
          onChange={handleType}
          onFocus={handleFocus}
          placeholder={placeholder}
          className="bg-[#121212] border-white/10 text-white pl-9 focus:border-[#0047FF]/40"
          data-testid={testId}
          autoComplete="off"
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 animate-spin" />
        )}
      </div>

      {open && items.length > 0 && (
        <div
          className="absolute z-50 top-full left-0 right-0 mt-1 rounded-xl bg-[#121212] border border-white/10 shadow-[0_12px_40px_-8px_rgba(0,0,0,0.6)] max-h-72 overflow-y-auto"
          data-testid={testId ? `${testId}-suggestions` : undefined}
        >
          {showingRecents && (
            <div className="px-4 py-2 border-b border-white/5 flex items-center gap-2">
              <Clock className="w-3 h-3 text-white/40" />
              <span className="text-[10px] uppercase tracking-wider text-white/40 font-bold">Recent</span>
            </div>
          )}
          {items.map((sug) => (
            <button
              key={sug.id}
              type="button"
              onClick={() => handlePick(sug)}
              className="w-full text-left px-4 py-3 hover:bg-white/5 flex items-start gap-3 border-b border-white/5 last:border-0 transition-colors"
              data-testid={testId ? `${testId}-option-${sug.id}` : undefined}
            >
              {sug.isRecent ? (
                <Clock className="w-4 h-4 mt-0.5 flex-shrink-0 text-white/40" />
              ) : (
                <MapPin className={`w-4 h-4 mt-0.5 flex-shrink-0 ${accentClass}`} />
              )}
              <div className="min-w-0 flex-1">
                <p className="text-sm text-white truncate">{sug.short}</p>
                <p className="text-xs text-white/40 truncate">{sug.display}</p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
